import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Layout, Image, Type, Link, List, Donut as ButtonIcon, Grid, Columns, Smartphone, Monitor, Save, Eye, Upload, Download } from 'lucide-react';
import { Button } from '../ui/Button';

interface Block {
  id: string;
  type: 'text' | 'image' | 'button' | 'divider' | 'spacer';
  content: any;
}

interface EmailBuilderProps {
  isTemplate?: boolean;
}

const EmailBuilder: React.FC<EmailBuilderProps> = ({ isTemplate = false }) => {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    onDrop: (acceptedFiles) => {
      // Handle image upload
    }
  });

  const blockTypes = [
    { type: 'text', icon: <Type />, label: 'Text' },
    { type: 'image', icon: <Image />, label: 'Image' },
    { type: 'button', icon: <ButtonIcon />, label: 'Button' },
    { type: 'divider', icon: <Layout />, label: 'Divider' },
    { type: 'columns', icon: <Columns />, label: 'Columns' },
    { type: 'list', icon: <List />, label: 'List' }
  ];

  return (
    <div className="flex h-[calc(100vh-12rem)]">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Content Blocks</h3>
          <div className="grid grid-cols-2 gap-2">
            {blockTypes.map((block) => (
              <button
                key={block.type}
                className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => {/* Add block */}}
              >
                {block.icon}
                <span className="mt-1 text-xs">{block.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Templates</h3>
          <div className="space-y-2">
            {['Welcome', 'Newsletter', 'Promotion'].map((template) => (
              <button
                key={template}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-50"
              >
                {template}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Editor */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <div className="border-l border-gray-200 pl-4 flex items-center space-x-2">
                <button
                  className={`p-2 rounded-md ${
                    previewMode === 'desktop' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="h-5 w-5" />
                </button>
                <button
                  className={`p-2 rounded-md ${
                    previewMode === 'mobile' ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setPreviewMode('mobile')}
                >
                  <Smartphone className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 bg-gray-50 p-8 overflow-y-auto">
          <div 
            className={`bg-white rounded-lg shadow-sm mx-auto ${
              previewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'
            }`}
          >
            {blocks.length === 0 ? (
              <div className="p-8 text-center">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12">
                  <Layout className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Drag and drop content blocks or select a template to get started
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-8">
                {blocks.map((block) => (
                  <div
                    key={block.id}
                    className={`p-4 rounded-lg cursor-pointer ${
                      selectedBlock === block.id ? 'ring-2 ring-primary' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedBlock(block.id)}
                  >
                    {/* Block content */}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Properties Panel */}
      {selectedBlock && (
        <div className="w-64 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Properties</h3>
            {/* Block properties */}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailBuilder;